import React, { useState } from 'react';
import {
  Box, Typography, Popover, List, ListItem, ListItemText, TextField, InputAdornment, IconButton,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';

/**
 * Expression builder - variable picker for {{variable}} syntax.
 * Use in node config fields that support variable substitution.
 * @param {string} value - Current field value
 * @param {function} onChange - (newValue) => void
 * @param {object[]} variables - [{ name, description }] - available variables
 * @param {object} anchorEl - MUI anchor for popover, or use anchorRef
 * @param {function} onOpen - () => void - when picker opens
 */
export function ExpressionBuilder({ value = '', onChange, variables = [], anchorEl, onOpen }) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const [search, setSearch] = useState('');

  const filtered = variables.filter(
    (v) => !search || (v.name && v.name.toLowerCase().includes(search.toLowerCase())),
  );

  const handleInsert = (varName) => {
    const insert = `{{${varName}}}`;
    const newVal = typeof value === 'string' ? value + insert : insert;
    onChange(newVal);
    setOpen(false);
    setAnchor(null);
  };

  const handleAnchorClick = (e) => {
    setAnchor(anchorEl || e.currentTarget);
    setOpen(true);
    onOpen?.();
  };

  return (
    <>
      <IconButton size="small" onClick={handleAnchorClick} title="Insert variable" sx={{ p: 0.5 }}>
        <AddIcon fontSize="small" />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchor || anchorEl}
        onClose={() => { setOpen(false); setAnchor(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 1, minWidth: 220, maxHeight: 300 }}>
          <Typography variant="caption" color="text.secondary">Insert variable</Typography>
          <TextField
            size="small"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
            sx={{ mt: 0.5, mb: 1 }}
          />
          <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
            {filtered.length === 0 ? (
              <ListItem><ListItemText primary="No variables" /></ListItem>
            ) : (
              filtered.map((v) => (
                <ListItem
                  key={v.name}
                  button
                  onClick={() => handleInsert(v.name)}
                >
                  <ListItemText primary={`{{${v.name}}}`} secondary={v.description} />
                </ListItem>
              ))
            )}
          </List>
        </Box>
      </Popover>
    </>
  );
}

/**
 * Build default variable list from nodes and global state.
 */
export function buildVariableList(nodes = [], globalState = {}) {
  const vars = [
    { name: 'input', description: 'User input or flow input' },
    { name: 'userInput', description: 'User message' },
    { name: 'ai_response', description: 'Last AI response' },
    { name: 'rag_context', description: 'RAG retrieval context' },
    { name: 'apiResponse', description: 'API call response' },
  ];
  Object.keys(globalState).forEach((k) => {
    if (!vars.some((v) => v.name === k)) vars.push({ name: k, description: `State: ${k}` });
  });
  nodes.forEach((n) => {
    const outKey = n?.data?.config?.outputVariable || n?.data?.config?.stateVariable;
    if (outKey && !vars.some((v) => v.name === outKey)) {
      vars.push({ name: outKey, description: `From ${n.data?.label || n.id}` });
    }
  });
  return vars;
}

export default ExpressionBuilder;
